import { Link } from "react-router-dom";
import { useSiteData } from "@/context/SiteDataContext";
import { useT } from "@/lib/useSiteText";
import type { SiteTextKey } from "@/lib/siteTexts";

const quickLinks: { to: string; key: SiteTextKey }[] = [
  { to: "/bodegas", key: "comun.footer.link_bodegas" },
  { to: "/planes", key: "comun.footer.link_planes" },
  { to: "/ubicacion", key: "comun.footer.link_ubicacion" },
  { to: "/seguridad", key: "comun.footer.link_seguridad" },
  { to: "/nosotros", key: "comun.footer.link_nosotros" },
  { to: "/preguntas-frecuentes", key: "comun.footer.link_faq" },
  { to: "/contacto", key: "comun.footer.link_contacto" },
];

export default function Footer() {
  const { data, whatsappLink } = useSiteData();
  const t = useT();
  const { siteSettings } = data;
  const visitas = t("comun.footer.visitas");

  return (
    <footer className="bg-primary-950 text-background-100">
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <Link to="/" className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
                <i className="ri-store-2-line text-lg" />
              </span>
              <span className="font-heading font-bold text-lg text-background-50">{siteSettings.brand}</span>
            </Link>
            <p className="mt-4 text-sm text-background-300 leading-relaxed">{siteSettings.tagline}</p>
            <a
              href={siteSettings.instagram_url}
              target="_blank"
              rel="noreferrer"
              className="mt-5 inline-flex items-center gap-2 text-sm text-background-300 hover:text-background-50 transition"
            >
              <span className="w-8 h-8 rounded-md border border-background-700 flex items-center justify-center">
                <i className="ri-instagram-line text-base" />
              </span>
              {siteSettings.instagram}
            </a>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-background-50 mb-4">{t("comun.footer.enlaces_titulo")}</h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-background-300 hover:text-background-50 transition">
                    {t(link.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-background-50 mb-4">{t("comun.footer.contacto_titulo")}</h4>
            <ul className="space-y-3 text-sm text-background-300">
              <li className="flex items-start gap-2.5">
                <i className="ri-map-pin-2-line mt-0.5 text-primary-300" />
                <span>{siteSettings.address}</span>
              </li>
              <li className="flex items-center gap-2.5">
                <i className="ri-phone-line text-primary-300" />
                <a href={`tel:${siteSettings.phone_raw}`} className="hover:text-background-50 transition">
                  {siteSettings.phone_display}
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <i className="ri-whatsapp-line text-primary-300" />
                <a href={whatsappLink()} target="_blank" rel="noreferrer" className="hover:text-background-50 transition">
                  {t("comun.footer.whatsapp_directo")}
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <i className="ri-mail-line text-primary-300" />
                <a href={`mailto:${siteSettings.email}`} className="hover:text-background-50 transition">
                  {siteSettings.email}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-background-50 mb-4">{t("comun.horario.titulo")}</h4>
            <ul className="space-y-2.5 text-sm text-background-300">
              <li className="flex justify-between gap-4">
                <span>{t("comun.horario.lunes_viernes")}</span>
                <span className="text-background-100">{siteSettings.hours_weekday}</span>
              </li>
              <li className="flex justify-between gap-4">
                <span>{t("comun.horario.sabado")}</span>
                <span className="text-background-100">{siteSettings.hours_saturday}</span>
              </li>
              <li className="flex justify-between gap-4">
                <span>{t("comun.horario.acceso")}</span>
                <span className="text-background-100">{siteSettings.hours_units}</span>
              </li>
            </ul>
            {visitas && <p className="mt-5 text-xs text-background-400 leading-relaxed">{visitas}</p>}
          </div>
        </div>
      </div>

      <div className="border-t border-background-800">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-background-400">
            {t("comun.footer.copyright", { anio: new Date().getFullYear(), marca: siteSettings.brand })}
          </p>
          <div className="flex items-center gap-5">
            <Link to="/terminos" className="text-xs text-background-400 hover:text-background-100 transition">
              {t("comun.footer.terminos")}
            </Link>
            <Link to="/privacidad" className="text-xs text-background-400 hover:text-background-100 transition">
              {t("comun.footer.privacidad")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}